/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {ArrowUturnLeftIcon, PaperAirplaneIcon, PencilIcon, TrashIcon, XMarkIcon,} from '@heroicons/react/24/outline'

import {useAppDispatch} from 'helpers/store'
import Dates from 'utils/Dates'
import {DATE_FORMATS} from 'constants/DateFormats'

import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import {closeSecondaryModal, openSecondaryModal} from 'helpers/actions'
import {ModalSize} from 'types/enums/ModalSize'
import {ReservationNote} from 'types/entities/ReservationNote'
import UiInput from 'components/Ui/UiInput'
import ReservationsAction from 'store/reservations/ReservationsAction'
import {toast} from 'utils/toastUtils'
import {useTypedSelector} from 'helpers/reducers'
import {IS_DEV} from "helpers/config";

interface BookingSellerReservationNotesTableProps {
    notes: ReservationNote[]
    title?: string
    className?: string
    reservationId: number
}

const BookingSellerReservationNotesTable = (
    props: BookingSellerReservationNotesTableProps
) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [inputValue, setInputValue] = useState('')
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [replyingTo, setReplyingTo] = useState<ReservationNote | null>(null)
    const scrollEndRef = useRef<HTMLDivElement>(null)

    const currentUser = useTypedSelector((state) => state.sessionState.session)

    const scrollToBottom = () => {
        scrollEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [props.notes, replyingTo, editingNoteId])

    const onEditHandler = (note: ReservationNote) => {
        setInputValue(note.note)
        setEditingNoteId(note.id)
        setReplyingTo(null)
        const input = document.getElementById('reservation-note-input')
        if (input) input.focus()
    }

    const onDeleteHandler = (noteId: number) => {
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Delete Note')}
                    message={t('Are you sure you want to delete this note?')}
                    onConfirm={async () => {
                        try {
                            await dispatch(
                                ReservationsAction.fetchDeleteReservationNote(
                                    props.reservationId,
                                    noteId
                                )
                            )
                            toast.success(t('Note deleted'))
                            dispatch(closeSecondaryModal())
                        } catch (error) {
                            toast.error(t('Failed to delete note'))
                            dispatch(closeSecondaryModal())
                        }
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    const onSubmitHandler = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        if (!inputValue.trim() || isLoading) return

        setIsLoading(true)
        try {
            if (editingNoteId) {
                await dispatch(
                    ReservationsAction.fetchEditReservationNote(
                        editingNoteId,
                        inputValue
                    )
                )
                toast.success(t('Note updated'))
                setEditingNoteId(null)
            } else {
                await dispatch(
                    ReservationsAction.fetchAddReservationNote(
                        props.reservationId,
                        inputValue,
                        replyingTo?.id
                    )
                )
                toast.success(t('Note added'))
            }
            setInputValue('')
            setReplyingTo(null)
        } catch (error) {
            toast.error(t('Failed to save note'))
        } finally {
            setIsLoading(false)
        }
    }

    const getInitials = (name?: string) => {
        if (!name) return '?'
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    const getThreadedNotes = () => {
        const rootNotes: ReservationNote[] = []
        const repliesMap = new Map<number, ReservationNote[]>()

        const sortedNotes = [...props.notes].sort((a, b) => {
            const dateA = Dates.dayjs(a.created_at)
            const dateB = Dates.dayjs(b.created_at)
            return dateA.diff(dateB)
        })

        sortedNotes.forEach((note) => {
            if (note.replied_note_id) {
                const list = repliesMap.get(note.replied_note_id) || []
                list.push(note)
                repliesMap.set(note.replied_note_id, list)
            } else {
                rootNotes.push(note)
            }
        })

        return { rootNotes, repliesMap }
    }

    const { rootNotes, repliesMap } = getThreadedNotes()
    const displayTitle = props.title ?? t('BackOffice Notes')

    const renderNote = (note: ReservationNote, isReply = false) => {
        const isCreator =
			IS_DEV ||
            currentUser?.uid === note.creator_id ||
            currentUser?.uid === note.creator?.id
        const creatorName =
            note.creator?.name || (isCreator ? t('You') : t('Unknown'))

        const noteReplies = repliesMap.get(note.id) || []

        return (
            <div
                key={note.id}
                className={clsx(
                    'RenderNote flex flex-col',
                    isReply && 'ml-4 mt-1'
                )}
            >
                <div
                    className={clsx(
                        'group flex gap-2 py-1.5 px-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border border-transparent hover:border-gray-100 dark:hover:border-gray-800 rounded-lg relative'
                    )}
                >
                    <div className="shrink-0">
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-300">
                            {getInitials(creatorName)}
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                            <div className="flex items-start gap-x-2">
                                <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                                    {creatorName}
                                </span>
                                <span className="text-[10px] text-gray-400">
                                    {Dates.dayjs(note.created_at).format(
                                        DATE_FORMATS.DATE_TIME
                                    )}
                                </span>
                            </div>

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => {
                                        setReplyingTo(note)
                                        setEditingNoteId(null)
                                        setInputValue('')
                                        setTimeout(() => {
                                            const input =
                                                document.getElementById(
                                                    'reservation-note-input'
                                                )
                                            if (input) input.focus()
                                        }, 50)
                                    }}
                                    className="p-1 text-gray-400 hover:text-blue-500 rounded transition-colors"
                                    title={t('Reply') as string}
                                >
                                    <ArrowUturnLeftIcon className="size-3" />
                                </button>
                                {isCreator && (
                                    <>
                                        <button
                                            onClick={() => onEditHandler(note)}
                                            className="p-1 text-gray-400 hover:text-blue-500 rounded transition-colors"
                                            title={t('Edit')}
                                        >
                                            <PencilIcon className="w-3" />
                                        </button>
                                        <button
                                            onClick={() =>
                                                onDeleteHandler(note.id)
                                            }
                                            className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                                            title={t('Delete')}
                                        >
                                            <TrashIcon className="w-3" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="text-xs text-gray-800 dark:text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
							<span dangerouslySetInnerHTML={{__html: note.note}} />
                        </div>
                    </div>
                </div>
                {noteReplies.length > 0 && (
                    <div className="flex flex-col border-l-2 border-gray-100 dark:border-gray-800 ml-5">
                        {noteReplies.map((reply) => renderNote(reply, true))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div
            className={clsx(
                'BookingSellerReservationNotesTable UiCard p-2 flex flex-col h-full',
                props.className
            )}
        >
            <div className="flex items-center justify-between mb-2 pb-1 border-b border-gray-200 dark:border-gray-700 shrink-0">
                <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {displayTitle}
                </h3>
            </div>

            <div className="ReservationNotes flex-1 min-h-0 overflow-y-auto mb-2 px-1 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                <div className="flex flex-col gap-1">
                    {rootNotes.length === 0 && (
                        <div className="text-center text-gray-400 text-xs py-4 italic">
                            {t('No notes yet. Type a message to start.')}
                        </div>
                    )}
                    {rootNotes.map((note) => renderNote(note))}
                    <div ref={scrollEndRef} />
                </div>
            </div>

            <div className="flex flex-col gap-2 shrink-0 border-t border-gray-100 dark:border-gray-800 pt-2">
                {replyingTo && (
                    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 px-2 py-1.5 rounded-md text-xs border border-blue-100 dark:border-blue-900/30">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <ArrowUturnLeftIcon className="size-3 text-blue-500" />
                            <span className="text-gray-500 dark:text-gray-400 truncate">
                                {t('Replying to')}{' '}
                                <span className="font-semibold text-blue-600 dark:text-blue-400">
                                    {replyingTo.creator?.name || t('Unknown')}
                                </span>
                                : "{replyingTo.note.substring(0, 30)}
                                {replyingTo.note.length > 30 ? '...' : ''}"
                            </span>
                        </div>
                        <button
                            onClick={() => setReplyingTo(null)}
                            className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <XMarkIcon className="size-3" />
                        </button>
                    </div>
                )}

                {editingNoteId && (
                    <div className="flex items-center justify-between bg-orange-50 dark:bg-orange-900/20 px-2 py-1.5 rounded-md text-xs border border-orange-100 dark:border-orange-900/30">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <PencilIcon className="size-3 text-orange-500" />
                            <span className="text-gray-500 dark:text-gray-400 truncate">
                                {t('Editing note')}
                            </span>
                        </div>
                        <button
                            onClick={() => {
                                setEditingNoteId(null)
                                setInputValue('')
                            }}
                            className="p-0.5 hover:bg-orange-100 dark:hover:bg-orange-800 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <XMarkIcon className="size-3" />
                        </button>
                    </div>
                )}

                <form
                    onSubmit={onSubmitHandler}
                    className="flex gap-2 items-center"
                >
                    <div className="flex-1">
                        <UiInput
                            id="reservation-note-input"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={
                                replyingTo
                                    ? (t('Type your response...') as string)
                                    : (t('Type a note...') as string)
                            }
                            className="w-full text-xs"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading || !inputValue.trim()}
                        className={clsx(
                            'p-2 rounded-lg transition-colors text-white disabled:opacity-50 disabled:cursor-not-allowed',
                            editingNoteId
                                ? 'bg-orange-500 hover:bg-orange-600'
                                : 'bg-blue-500 hover:bg-blue-600',
                            isLoading && 'opacity-50 cursor-not-allowed'
                        )}
                        title={editingNoteId ? t('Update') : t('Send')}
                    >
                        <PaperAirplaneIcon className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </div>
    )
}

export default BookingSellerReservationNotesTable
